import api from '../../apiClient';

interface SalesInvoiceListParams {
  page?: number;
  page_size?: number;
  search?: string | null;
  status?: string | null;
  customer_id?: number | null;
  invoice_type?: string | null;
  date_from?: string | null;
  date_to?: string | null;
}

interface SalesInvoiceRequest {
  invoice_number: string;
  invoice_date: string;
  customer_id: number;
  warehouse_id?: number;
  agency_id?: string;
  due_date?: string;
  items: any[];
  payment_details?: any[];
  [key: string]: any;
}

interface SalesInvoiceResponse {
  id: number;
  invoice_number: string;
  invoice_date: string;
  customer_id: number;
  customer_name?: string;
  due_date?: string;
  total_amount: number;
  status: string;
  [key: string]: any;
}

interface SalesInvoiceListResponse {
  data: SalesInvoiceResponse[];
  total: number;
  page: number;
  page_size: number;
}

export const salesInvoiceService = {
  /**
   * Get all sales invoices with pagination and filters
   */
  async getSalesInvoices(params: SalesInvoiceListParams = {}) {
    try {
      const response = await api.get<SalesInvoiceListResponse>(
        '/api/v1/inventory/sales-invoices',
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch sales invoices:', error);
      throw error;
    }
  },

  /**
   * Get a specific sales invoice by ID
   */
  async getSalesInvoice(invoiceId: number) {
    try {
      const response = await api.get<SalesInvoiceResponse>(
        `/api/v1/inventory/sales-invoices/${invoiceId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch sales invoice ${invoiceId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new sales invoice
   */
  async createSalesInvoice(invoiceData: SalesInvoiceRequest) {
    try {
      const response = await api.post<SalesInvoiceResponse>(
        '/api/v1/inventory/sales-invoices',
        invoiceData
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create sales invoice:', error);
      throw error;
    }
  },

  /**
   * Update an existing sales invoice
   */
  async updateSalesInvoice(invoiceId: number, invoiceData: SalesInvoiceRequest) {
    try {
      const response = await api.put<SalesInvoiceResponse>(
        `/api/v1/inventory/sales-invoices/${invoiceId}`,
        invoiceData
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to update sales invoice ${invoiceId}:`, error);
      throw error;
    }
  },

  /**
   * Delete (soft delete) a sales invoice
   */
  async deleteSalesInvoice(invoiceId: number) {
    try {
      const response = await api.delete(
        `/api/v1/inventory/sales-invoices/${invoiceId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to delete sales invoice ${invoiceId}:`, error);
      throw error;
    }
  },

  /**
   * Record a payment for a sales invoice
   * @param invoiceId - The invoice ID
   * @param paymentAmount - The payment amount
   */
  async recordPayment(invoiceId: number, paymentAmount: number) {
    try {
      const response = await api.post<SalesInvoiceResponse>(
        `/api/v1/inventory/sales-invoices/${invoiceId}/payment`,
        {},
        { params: { payment_amount: paymentAmount } }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to record payment for invoice ${invoiceId}:`, error);
      throw error;
    }
  }
};
